import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../components/auth/AuthContext";
import { HeaderBar } from "../components/layout/TopBar";
import NavBar from "../components/layout/NavBar";
import { FolderTreeArt } from "../components/adminSurface/heroArt";
import { Hero, Dropdown, ToggleSwitch, SegControl, EmptyState } from "../components/adminSurface/AdminSurface";
import { useIsAdmin } from "../hooks/useFeatureFlags";
import {
  getDatabases,
  getStorageLocations,
  getLegalCases,
  getLegalCase,
  createLegalCase,
  closeLegalCase,
  previewLegalCollect,
  runLegalCollect,
  getLegalCaseItems,
  codeLegalItems,
  getPrivilegeLog,
  getProductions,
  proposeProduction,
  previewProduction,
  approveProduction,
  rejectProduction,
  produceProduction,
  getProductionCertificate,
  getDatabaseIndexes,
  createLegalCustodian,
  updateLegalCustodian,
  deactivateLegalCustodian,
  getCustodianFieldMaps,
  createCustodianFieldMap,
  updateCustodianFieldMap,
  deactivateCustodianFieldMap,
} from "../api/adminApi";

// Page-specific bits ONLY — the hero, cards, fields, buttons, chips and empty
// states all come from the shared admin-surface kit (styles/adminSurface.css +
// components/adminSurface) so eDiscovery reads as the same product as Legal
// Holds / Records Management rather than a page of its own invention.
const ED_STYLES = `
.ed-grid{ display:grid; grid-template-columns:340px 1fr; gap:18px; align-items:start; }
@media (max-width:1080px){ .ed-grid{ grid-template-columns:1fr; } }

.ed-cardhd{ display:flex; align-items:center; justify-content:space-between; gap:10px; margin:0 0 14px; flex-wrap:wrap; }
.ed-cardhd h2{ margin:0; font-size:15px; font-weight:800; color:#1e293b; letter-spacing:-.01em;
  display:inline-flex; align-items:center; gap:9px; min-width:0; }
.ed-hint{ margin:0 0 14px; font-size:12.5px; color:#94a3b8; line-height:1.55; }

.ed-list{ list-style:none; margin:0; padding:0; max-height:420px; overflow:auto; }
.ed-list li{ padding:10px 11px; border-radius:10px; cursor:pointer; font-size:13.5px; color:#334155;
  display:flex; align-items:center; justify-content:space-between; gap:8px; transition:background .12s, color .12s; }
.ed-list li:hover{ background:#f6f2ff; color:#1e293b; }
.ed-list li[aria-selected="true"]{ background:#f4f2fb; color:#6d28d9; font-weight:600; box-shadow:inset 2px 0 0 #7c3aed; }
.ed-meta{ font-size:11px; color:#94a3b8; font-weight:500; }

.ed-counts{ display:flex; gap:10px; flex-wrap:wrap; margin-bottom:14px; }
.ed-count{ flex:1 1 96px; background:linear-gradient(135deg,#f8fafc,#f6f2ff); border:1px solid #eef0f5;
  border-radius:12px; padding:10px 13px; }
.ed-count b{ display:block; font-size:19px; font-weight:800; color:#0f172a; line-height:1.25; }
.ed-count span{ font-size:10.5px; font-weight:700; text-transform:uppercase; letter-spacing:.06em; color:#64748b; }

.ed-scroll{ overflow-x:auto; border:1px solid #eef0f5; border-radius:14px; }
.ed-table{ width:100%; border-collapse:collapse; font-size:13px; }
.ed-table th{ text-align:left; padding:11px 14px; background:#f8fafc; color:#64748b; font-size:10.5px;
  font-weight:700; text-transform:uppercase; letter-spacing:.07em; border-bottom:1px solid #eef0f5; white-space:nowrap; }
.ed-table td{ padding:11px 14px; border-bottom:1px solid #f4f6fa; color:#334155; vertical-align:middle; }
.ed-table tr:last-child td{ border-bottom:none; }
.ed-table tbody tr:hover td{ background:#faf7ff; }

.ed-pill{ display:inline-flex; align-items:center; gap:5px; padding:3px 10px; border-radius:999px;
  font-size:11px; font-weight:700; letter-spacing:.02em; border:1px solid transparent; white-space:nowrap; }
.ed-pill--open{ background:#ecfdf5; color:#047857; border-color:#a7f3d0; }
.ed-pill--closed{ background:#f1f5f9; color:#64748b; border-color:#e2e8f0; }
.ed-pill--needsreview{ background:#f1f5f9; color:#64748b; border-color:#e2e8f0; }
.ed-pill--responsive{ background:#eff6ff; color:#1d4ed8; border-color:#bfdbfe; }
.ed-pill--notresponsive{ background:#f8fafc; color:#57534e; border-color:#e7e5e4; }
.ed-pill--privileged{ background:#fef2f2; color:#b91c1c; border-color:#fecaca; }
.ed-pill--proposed{ background:#fffbeb; color:#b45309; border-color:#fde68a; }
.ed-pill--approved{ background:#eff6ff; color:#1d4ed8; border-color:#bfdbfe; }
.ed-pill--produced{ background:#ecfdf5; color:#047857; border-color:#a7f3d0; }
.ed-pill--rejected{ background:#fef2f2; color:#b91c1c; border-color:#fecaca; }

.ed-note{ border-radius:12px; padding:12px 14px; font-size:12.5px; line-height:1.55; margin-bottom:14px; }
.ed-note--warn{ background:#fffbeb; border:1px solid #fde68a; color:#92400e; }
.ed-note--ok{ background:#ecfdf5; border:1px solid #a7f3d0; color:#065f46; }

/* Compact variants of the kit buttons, for table rows and inline toolbars. */
.ed-sm{ padding:7px 13px !important; font-size:12.5px !important; border-radius:9px !important; }
.ed-danger{ background:linear-gradient(135deg,#b91c1c,#dc2626 60%,#ef4444); color:#fff; border:none;
  box-shadow:0 8px 20px -6px rgba(220,38,38,.42); }
.ed-danger:hover{ filter:brightness(1.06); background:linear-gradient(135deg,#b91c1c,#dc2626 60%,#ef4444); color:#fff; }
.ed-actions{ display:flex; gap:6px; flex-wrap:wrap; }

/* Custodian rows — a person, what they match on, and what they contributed. */
.ed-cust{ display:flex; align-items:flex-start; gap:12px; padding:11px 0; border-bottom:1px solid #f4f6fa; }
.ed-cust:last-child{ border-bottom:none; }
.ed-cust__body{ flex:1; min-width:0; }
.ed-cust__name{ font-size:13.5px; font-weight:700; color:#0f172a; }
.ed-cust__ids{ font-size:11.5px; color:#64748b; font-family:ui-monospace, monospace; word-break:break-word; margin-top:3px; }
.ed-cust__role{ font-size:11px; color:#94a3b8; margin-top:2px; }
.ed-cust__count{ flex:0 0 auto; text-align:right; }
.ed-cust__count b{ display:block; font-size:15px; font-weight:800; color:#0f172a; }
.ed-cust__count span{ font-size:10px; font-weight:700; text-transform:uppercase; letter-spacing:.06em; color:#94a3b8; }
.ed-cust--off{ opacity:.55; }

/* Custodian picker inside the collection card. */
.ed-pick{ display:flex; flex-direction:column; gap:6px; max-height:180px; overflow:auto; padding:10px 12px;
  border:1px solid #e2e8f0; border-radius:10px; background:#fcfcfe; }
.ed-pick label{ display:flex; align-items:center; gap:9px; font-size:13px; color:#334155; cursor:pointer; }
.ed-pick input{ accent-color:#7c3aed; width:15px; height:15px; }
.ed-pick__meta{ margin-left:auto; font-size:11px; color:#94a3b8; font-family:ui-monospace, monospace; }

/* A custodian that matched nothing — the one preview signal that must not be quiet. */
.ed-zero{ background:#fffbeb; }
.ed-zero td{ color:#92400e !important; }
`;

/** Colour-coded status/coding pill. Unknown values render neutrally rather than being mapped onto a known one. */
function Pill({ value }) {
  const key = String(value || "").toLowerCase();
  const known = ["open", "closed", "needsreview", "responsive", "notresponsive", "privileged",
    "proposed", "approved", "produced", "rejected"];
  const cls = known.includes(key) ? `ed-pill--${key}` : "";
  return <span className={`ed-pill ${cls}`}>{value}</span>;
}

export default function EDiscoveryPage() {
  const { t } = useTranslation();
  const { token } = useAuth();
  const isAdmin = useIsAdmin();

  const [databases, setDatabases] = useState([]);
  const [locations, setLocations] = useState([]);
  const [matters, setMatters] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [items, setItems] = useState([]);
  const [itemFilter, setItemFilter] = useState("");
  const [productions, setProductions] = useState([]);
  const [privilegeLog, setPrivilegeLog] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [collectPreview, setCollectPreview] = useState(null);

  // No free-text custodians field here on purpose. Custodians are structured rows added to the matter after it
  // exists (they need identifiers that match the data), and a second input sharing the same "Custodians" label
  // would read as though typing names there scoped a collection — which it never did.
  const [newMatter, setNewMatter] = useState({ name: "", matterNumber: "", itemCap: 250000 });
  const [collect, setCollect] = useState({ dbName: "", conditionJson: "" });

  // Custodian scope. "none" reproduces the pre-custodian behaviour exactly; "selected" needs at least one
  // custodian ticked before it can run, so an empty selection can never be read as "collect everything".
  const [custodians, setCustodians] = useState([]);
  const [scopeMode, setScopeMode] = useState("none");
  const [scopeIds, setScopeIds] = useState([]);
  const [newCustodian, setNewCustodian] = useState({ displayName: "", identifiers: "", role: "" });

  // Per-database custodian field mapping (DIXI_ADMIN only).
  const [fieldMaps, setFieldMaps] = useState([]);
  const [mapDbName, setMapDbName] = useState("");
  const [mapColumns, setMapColumns] = useState([]);
  const [newMap, setNewMap] = useState({ fieldName: "", matchMode: "Exact", description: "" });
  const [prod, setProd] = useState({
    name: "", recipient: "", includeCodings: "Responsive",
    excludePrivileged: true, applyRedactions: true, batesPrefix: "", batesStart: 1, targetLocationId: "",
  });

  const fail = useCallback((e, fallbackKey) => {
    // Surface the server's own code + message: every guard returns a stable code, and telling the
    // operator which one fired is far more useful than a generic failure banner.
    const msg = e?.body?.message || e?.message || t(fallbackKey);
    const code = e?.body?.code;
    setError(code ? `${code}: ${msg}` : msg);
    setNotice("");
  }, [t]);

  const loadMatters = useCallback(async () => {
    if (!token) return;
    try {
      const rows = await getLegalCases(token);
      setMatters(Array.isArray(rows) ? rows : []);
    } catch (e) { fail(e, "eDiscovery.loadFailed"); }
  }, [token, fail]);

  const loadDetail = useCallback(async (id) => {
    if (!token || !id) return;
    setBusy(true);
    try {
      const [d, its, prods, plog] = await Promise.all([
        getLegalCase(token, id),
        getLegalCaseItems(token, id, { coding: itemFilter || undefined, take: 200 }),
        getProductions(token, id),
        getPrivilegeLog(token, id),
      ]);
      setDetail(d);
      setItems(its?.items || []);
      setProductions(Array.isArray(prods) ? prods : []);
      setPrivilegeLog(Array.isArray(plog) ? plog : []);
      const cust = Array.isArray(d?.custodians) ? d.custodians : [];
      setCustodians(cust);
      // Drop any ticked custodian that no longer exists, so a stale selection cannot widen a later collect.
      setScopeIds((prev) => prev.filter((id) => cust.some((c) => c.id === id)));
    } catch (e) { fail(e, "eDiscovery.loadFailed"); }
    finally { setBusy(false); }
  }, [token, itemFilter, fail]);

  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const [dbs, locs] = await Promise.all([getDatabases(token), getStorageLocations(token)]);
        setDatabases(Array.isArray(dbs) ? dbs : []);
        setLocations(Array.isArray(locs) ? locs : (locs?.data || []));
      } catch { /* non-fatal: the pickers simply stay empty */ }
      loadMatters();
    })();
  }, [token, loadMatters]);

  useEffect(() => { if (selectedId) loadDetail(selectedId); }, [selectedId, loadDetail]);

  const matter = detail?.matter;
  const isClosed = String(matter?.status || "").toLowerCase() === "closed";
  const coding = detail?.coding || {};

  const openCount = useMemo(
    () => matters.filter((m) => String(m.status || "").toLowerCase() === "open").length, [matters]);

  // Items in the review set that no custodian accounts for — an unscoped collection, or one run before
  // custodian scoping existed. Derived rather than fetched, and shown so the two numbers always reconcile.
  const unattributed = useMemo(() => {
    const attributed = custodians.reduce((n, c) => n + (c.collectedItems || 0), 0);
    return Math.max(0, (matter?.itemCount || 0) - attributed);
  }, [custodians, matter]);

  // What the collect/preview request carries. "selected" with nothing ticked is NOT sent as a scope — the
  // buttons are disabled instead, so an empty selection can never silently become an unscoped sweep.
  const scopeBody = useMemo(() => {
    if (scopeMode === "all") return { scopeToAllCustodians: true };
    if (scopeMode === "selected" && scopeIds.length > 0) return { custodianIds: scopeIds };
    return {};
  }, [scopeMode, scopeIds]);

  const scopeIncomplete = scopeMode === "selected" && scopeIds.length === 0;
  const activeCustodians = useMemo(() => custodians.filter((c) => c.isActive !== false), [custodians]);

  const loadFieldMaps = useCallback(async (dbName) => {
    if (!token || !isAdmin) return;
    const db = databases.find((d) => d.name === dbName);
    try {
      const rows = await getCustodianFieldMaps(token, { databaseId: db?.id, includeInactive: true });
      setFieldMaps(Array.isArray(rows) ? rows : []);
    } catch (e) { fail(e, "eDiscovery.loadFailed"); }

    // The column picker offers this database's SEARCHABLE index columns — custodian identity lives in one of
    // them, and offering the whole table would invite mapping something that is not indexed at all.
    if (!db?.id) { setMapColumns([]); return; }
    try {
      const idx = await getDatabaseIndexes(token, db.id);
      const list = Array.isArray(idx) ? idx : (idx?.data || idx?.indexes || []);
      setMapColumns(list.map((c) => c.fieldName || c.FieldName || c.name).filter(Boolean));
    } catch { setMapColumns([]); }
  }, [token, isAdmin, databases, fail]);

  useEffect(() => { if (mapDbName) loadFieldMaps(mapDbName); }, [mapDbName, loadFieldMaps]);

  async function onAddCustodian(e) {
    e.preventDefault();
    if (!newCustodian.displayName.trim()) return;
    setBusy(true); setError(""); setNotice("");
    try {
      await createLegalCustodian(token, selectedId, newCustodian);
      setNewCustodian({ displayName: "", identifiers: "", role: "" });
      setNotice(t("eDiscovery.custodianSaved"));
      await loadDetail(selectedId);
    } catch (e2) { fail(e2, "eDiscovery.saveFailed"); }
    finally { setBusy(false); }
  }

  async function onEditCustodianIdentifiers(c) {
    const next = window.prompt(t("eDiscovery.custodianIdentifiersPrompt"), c.identifiers || "");
    if (next === null) return;
    setBusy(true); setError(""); setNotice("");
    try {
      await updateLegalCustodian(token, selectedId, c.id, { identifiers: next });
      setNotice(t("eDiscovery.custodianSaved"));
      await loadDetail(selectedId);
    } catch (e) { fail(e, "eDiscovery.saveFailed"); }
    finally { setBusy(false); }
  }

  async function onRemoveCustodian(c) {
    if (!window.confirm(t("eDiscovery.confirmCustodianRemove", { name: c.displayName }))) return;
    setBusy(true); setError(""); setNotice("");
    try {
      const r = await deactivateLegalCustodian(token, selectedId, c.id);
      setNotice(t("eDiscovery.custodianRemoved", { items: r?.collectedItemsRetained ?? 0 }));
      setScopeIds((prev) => prev.filter((id) => id !== c.id));
      await loadDetail(selectedId);
    } catch (e) { fail(e, "eDiscovery.saveFailed"); }
    finally { setBusy(false); }
  }

  async function onAddFieldMap(e) {
    e.preventDefault();
    if (!mapDbName || !newMap.fieldName) return;
    setBusy(true); setError(""); setNotice("");
    try {
      const saved = await createCustodianFieldMap(token, { dbName: mapDbName, ...newMap });
      setNewMap({ fieldName: "", matchMode: "Exact", description: "" });
      // The advisory is present only when the mapped column is compared under Turkish rules, where i/İ and
      // ı/I are different LETTERS and so no match mode bridges them. Appended to the success notice because
      // the mapping DID save — this is a caveat about matching, not a failure.
      setNotice(saved?.advisory
        ? `${t("eDiscovery.fieldMapSaved")} — ${t("eDiscovery.turkishCollation", { defaultValue: saved.advisory.message, column: saved.advisory.fieldName })}`
        : t("eDiscovery.fieldMapSaved"));
      await loadFieldMaps(mapDbName);
    } catch (e2) { fail(e2, "eDiscovery.saveFailed"); }
    finally { setBusy(false); }
  }

  async function onChangeFieldMapMode(m, mode) {
    if (!mode || mode === m.matchMode) return;
    setBusy(true); setError(""); setNotice("");
    try {
      // Editable in place rather than deactivate-and-recreate: the mode is a tuning decision an
      // administrator will revise after seeing a preview, and recreating the row would lose the note and
      // the audit trail of who mapped the column in the first place.
      const saved = await updateCustodianFieldMap(token, m.id, { matchMode: mode });
      // Re-advised on a mode change specifically: switching to EqualsIgnoreCase hoping it will bridge the
      // dotted and dotless forms is the exact mistake the advisory exists to head off.
      setNotice(saved?.advisory
        ? `${t("eDiscovery.fieldMapSaved")} — ${t("eDiscovery.turkishCollation", { defaultValue: saved.advisory.message, column: saved.advisory.fieldName })}`
        : t("eDiscovery.fieldMapSaved"));
      await loadFieldMaps(mapDbName);
    } catch (e) { fail(e, "eDiscovery.saveFailed"); }
    finally { setBusy(false); }
  }

  async function onRemoveFieldMap(m) {
    if (!window.confirm(t("eDiscovery.confirmFieldMapRemove", { column: m.fieldName }))) return;
    setBusy(true); setError(""); setNotice("");
    try {
      await deactivateCustodianFieldMap(token, m.id);
      await loadFieldMaps(mapDbName);
    } catch (e) { fail(e, "eDiscovery.saveFailed"); }
    finally { setBusy(false); }
  }

  async function onCreateMatter(e) {
    e.preventDefault();
    if (!newMatter.name.trim()) return;
    setBusy(true); setError(""); setNotice("");
    try {
      const created = await createLegalCase(token, newMatter);
      setNewMatter({ name: "", matterNumber: "", itemCap: 250000 });
      await loadMatters();
      setSelectedId(created?.id);
      setNotice(t("eDiscovery.matterCreated"));
    } catch (e2) { fail(e2, "eDiscovery.saveFailed"); }
    finally { setBusy(false); }
  }

  async function onPreviewCollect() {
    if (!collect.dbName) return;
    setBusy(true); setError(""); setNotice(""); setCollectPreview(null);
    try {
      const r = await previewLegalCollect(token, selectedId, { ...collect, ...scopeBody });
      setCollectPreview(r);
    } catch (e) { fail(e, "eDiscovery.collectFailed"); }
    finally { setBusy(false); }
  }

  async function onCollect() {
    if (!collect.dbName) return;
    setBusy(true); setError(""); setNotice("");
    try {
      const r = await runLegalCollect(token, selectedId, { ...collect, ...scopeBody });
      const collected = t("eDiscovery.collected", { collected: r.collected, already: r.alreadyPresent });
      // The items ARE collected and reviewable; they simply cannot be produced. Appending rather than
      // replacing keeps that distinction, instead of reading as a failed collection.
      setNotice(r.sourceClosed && r.sourceWarning
        ? `${collected} — ${t("eDiscovery.sourceClosed", { defaultValue: r.sourceWarning.message, db: r.sourceWarning.dbName })}`
        : collected);
      setCollectPreview(null);
      await Promise.all([loadDetail(selectedId), loadMatters()]);
    } catch (e) { fail(e, "eDiscovery.collectFailed"); }
    finally { setBusy(false); }
  }

  async function onCode(itemId, nextCoding) {
    setBusy(true); setError(""); setNotice("");
    try {
      const body = { itemId, coding: nextCoding };
      if (nextCoding === "Privileged") {
        const basis = window.prompt(t("eDiscovery.privilegeBasisPrompt"));
        // Refusing locally when the basis is blank keeps the server's PRIVILEGE_BASIS_REQUIRED
        // as a backstop rather than the first thing an operator meets.
        if (!basis || !basis.trim()) { setBusy(false); return; }
        body.privilegeBasis = basis.trim();
      }
      const current = items.find((i) => i.id === itemId);
      if (current && String(current.coding).toLowerCase() === "privileged" && nextCoding !== "Privileged") {
        // The one transition that EXPOSES a document. Confirmed explicitly here and re-checked server-side.
        if (!window.confirm(t("eDiscovery.confirmPrivilegeRemoval"))) { setBusy(false); return; }
        body.acknowledgePrivilegeRemoval = true;
      }
      await codeLegalItems(token, selectedId, body);
      await loadDetail(selectedId);
    } catch (e) { fail(e, "eDiscovery.saveFailed"); }
    finally { setBusy(false); }
  }

  async function onProposeProduction(e) {
    e.preventDefault();
    setBusy(true); setError(""); setNotice("");
    try {
      const body = { ...prod, targetLocationId: prod.targetLocationId ? Number(prod.targetLocationId) : null };
      await proposeProduction(token, selectedId, body);
      setNotice(t("eDiscovery.productionProposed"));
      await loadDetail(selectedId);
    } catch (e2) { fail(e2, "eDiscovery.saveFailed"); }
    finally { setBusy(false); }
  }

  async function onProductionAction(setId, action) {
    setBusy(true); setError(""); setNotice("");
    try {
      if (action === "approve") {
        await approveProduction(token, selectedId, setId, { note: "" });
        setNotice(t("eDiscovery.productionApproved"));
      } else if (action === "reject") {
        const reason = window.prompt(t("eDiscovery.rejectPrompt")) || "";
        await rejectProduction(token, selectedId, setId, { reason });
        setNotice(t("eDiscovery.productionRejected"));
      } else if (action === "produce") {
        if (!window.confirm(t("eDiscovery.confirmProduce"))) { setBusy(false); return; }
        const r = await produceProduction(token, selectedId, setId);
        setNotice(t("eDiscovery.produced", {
          docs: r.docsProduced, withheld: r.docsWithheld, cert: r.certNumber,
        }));
      } else if (action === "preview") {
        const s = await previewProduction(token, selectedId, setId);
        setNotice(t("eDiscovery.scopePreview", {
          included: s.included, privileged: s.withheldPrivileged,
          unreviewed: s.withheldUnreviewed, outOfScope: s.withheldOutOfScope,
        }));
      } else if (action === "cert") {
        const c = await getProductionCertificate(token, selectedId, setId);
        setNotice(t("eDiscovery.certStatus", {
          cert: c.certNumber, valid: c.valid ? t("eDiscovery.certValid") : t("eDiscovery.certInvalid"),
          reason: c.reason,
        }));
      }
      await loadDetail(selectedId);
    } catch (e) { fail(e, "eDiscovery.saveFailed"); }
    finally { setBusy(false); }
  }

  async function onCloseMatter() {
    if (!window.confirm(t("eDiscovery.confirmClose"))) return;
    setBusy(true); setError(""); setNotice("");
    try {
      const reason = window.prompt(t("eDiscovery.closeReasonPrompt")) || "";
      const r = await closeLegalCase(token, selectedId, { reason });
      setNotice(t("eDiscovery.matterClosed", { holds: r.holdsLifted }));
      await Promise.all([loadDetail(selectedId), loadMatters()]);
    } catch (e) { fail(e, "eDiscovery.saveFailed"); }
    finally { setBusy(false); }
  }

  const codingOptions = ["NeedsReview", "Responsive", "NotResponsive", "Privileged"];

  return (
    <div className="app-root">
      <style>{ED_STYLES}</style>
      <div className="nav-strip"><NavBar /></div>
      <HeaderBar />

      <div className="page-container" style={{ paddingBottom: 40 }}>
        <Hero
          title={t("eDiscovery.title")}
          subtitle={t("eDiscovery.subtitle")}
          statValue={openCount}
          statLabel={t("eDiscovery.openMatters")}
          logo={<FolderTreeArt />}
          onRefresh={() => { loadMatters(); if (selectedId) loadDetail(selectedId); }}
          refreshing={busy}
        />

        {error && <div className="asf-alert"><span style={{ fontSize: 16 }}>⚠</span> {error}</div>}
        {notice && <div className="ed-note ed-note--ok">{notice}</div>}

        <div className="ed-grid">
          {/* ── Matters ── */}
          <div>
            <div className="asf-card">
              <div className="ed-cardhd"><h2>{t("eDiscovery.matters")}</h2>
                {matters.length > 0 && <span className="asf-count">{matters.length}</span>}
              </div>
              {matters.length === 0
                ? <EmptyState icon="⚖️" title={t("eDiscovery.noMatters")} />
                : (
                  <ul className="ed-list" role="listbox">
                    {matters.map((m) => (
                      <li key={m.id} role="option" aria-selected={m.id === selectedId} onClick={() => setSelectedId(m.id)}>
                        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {m.name}
                          <span className="ed-meta"> {m.matterNumber || ""}</span>
                        </span>
                        <Pill value={m.status} />
                      </li>
                    ))}
                  </ul>
                )}
            </div>

            <div className="asf-card">
              <div className="ed-cardhd"><h2>{t("eDiscovery.newMatter")}</h2></div>
              <form onSubmit={onCreateMatter}>
                <div className="asf-field" style={{ marginBottom: 12 }}>
                  <label className="asf-lbl">{t("eDiscovery.matterName")}</label>
                  <input className="asf-input" value={newMatter.name} required
                    onChange={(e) => setNewMatter({ ...newMatter, name: e.target.value })} />
                </div>
                <div className="asf-field" style={{ marginBottom: 12 }}>
                  <label className="asf-lbl">{t("eDiscovery.matterNumber")}</label>
                  <input className="asf-input" value={newMatter.matterNumber}
                    onChange={(e) => setNewMatter({ ...newMatter, matterNumber: e.target.value })} />
                </div>
                <div className="asf-field" style={{ marginBottom: 12 }}>
                  <label className="asf-lbl">{t("eDiscovery.itemCap")}</label>
                  <input className="asf-input" type="number" min="1" value={newMatter.itemCap}
                    onChange={(e) => setNewMatter({ ...newMatter, itemCap: Number(e.target.value) })} />
                </div>
                <p className="ed-hint">{t("eDiscovery.itemCapHint")}</p>
                <button className="asf-cta" type="submit" disabled={busy}>{t("eDiscovery.createMatter")}</button>
              </form>
            </div>
          </div>

          {/* ── Detail ── */}
          <div>
            {!matter ? (
              <div className="asf-card"><EmptyState icon="📂" title={t("eDiscovery.selectMatter")} /></div>
            ) : (
              <>
                <div className="asf-card">
                  <div className="ed-cardhd">
                    <h2>{matter.name} <Pill value={matter.status} /></h2>
                    {!isClosed && (
                      <button className="asf-btn ed-sm" onClick={onCloseMatter} disabled={busy}>{t("eDiscovery.closeMatter")}</button>
                    )}
                  </div>
                  {isClosed && <div className="ed-note ed-note--warn">{t("eDiscovery.closedNotice")}</div>}
                  <div className="ed-counts">
                    {codingOptions.map((c) => (
                      <div className="ed-count" key={c}>
                        <b>{coding[c] ?? 0}</b>
                        <span>{t(`eDiscovery.coding.${c}`)}</span>
                      </div>
                    ))}
                    <div className="ed-count">
                      <b>{matter.itemCount}</b><span>{t("eDiscovery.itemsCollected")}</span>
                    </div>
                    <div className="ed-count">
                      <b>{matter.itemCap}</b><span>{t("eDiscovery.itemCap")}</span>
                    </div>
                  </div>
                  {(detail?.holds || []).length > 0 && (
                    <p className="ed-hint" style={{ margin: 0 }}>
                      {t("eDiscovery.holdsSummary", {
                        active: detail.holds.filter((h) => h.active).length,
                        total: detail.holds.length,
                      })}
                    </p>
                  )}
                </div>

                {/* Custodians — who is in scope, and what each of them matches on */}
                <div className="asf-card">
                  <div className="ed-cardhd">
                    <h2>{t("eDiscovery.custodians")}</h2>
                    {unattributed > 0 && (
                      <span className="asf-chip" title={t("eDiscovery.unattributedHint")}>
                        {t("eDiscovery.unattributedItems", { count: unattributed })}
                      </span>
                    )}
                  </div>
                  <p className="ed-hint">{t("eDiscovery.custodiansHint")}</p>

                  {activeCustodians.length === 0
                    ? <EmptyState icon="👤" title={t("eDiscovery.noCustodians")} hint={t("eDiscovery.noCustodiansHint")} />
                    : activeCustodians.map((c) => (
                      <div className="ed-cust" key={c.id}>
                        <div className="ed-cust__body">
                          <div className="ed-cust__name">{c.displayName}</div>
                          <div className="ed-cust__ids">
                            {c.identifiers
                              ? c.identifiers
                              : <span style={{ color: "#b45309", fontFamily: "inherit" }}>
                                  {t("eDiscovery.custodianNoIdentifiers")}
                                </span>}
                          </div>
                          {c.role && <div className="ed-cust__role">{c.role}</div>}
                        </div>
                        <div className="ed-cust__count">
                          <b>{c.collectedItems ?? 0}</b>
                          <span>{t("eDiscovery.custodianItems")}</span>
                        </div>
                        {!isClosed && (
                          <div className="ed-actions">
                            <button className="asf-btn ed-sm" disabled={busy}
                              onClick={() => onEditCustodianIdentifiers(c)}>{t("eDiscovery.custodianEditIds")}</button>
                            <button className="asf-btn ed-sm" disabled={busy}
                              onClick={() => onRemoveCustodian(c)}>{t("eDiscovery.custodianRemove")}</button>
                          </div>
                        )}
                      </div>
                    ))}

                  {!isClosed && (
                    <form onSubmit={onAddCustodian} style={{ marginTop: 16 }}>
                      <div className="asf-grid">
                        <div className="asf-field" style={{ flex: "1 1 170px" }}>
                          <label className="asf-lbl">{t("eDiscovery.custodianName")}</label>
                          <input className="asf-input" value={newCustodian.displayName}
                            onChange={(e) => setNewCustodian({ ...newCustodian, displayName: e.target.value })} />
                        </div>
                        <div className="asf-field" style={{ flex: "2 1 240px" }}>
                          <label className="asf-lbl">{t("eDiscovery.custodianIdentifiers")}</label>
                          <input className="asf-input" value={newCustodian.identifiers} placeholder="AYILMAZ;TR3300062…"
                            onChange={(e) => setNewCustodian({ ...newCustodian, identifiers: e.target.value })} />
                        </div>
                        <div className="asf-field" style={{ flex: "1 1 140px" }}>
                          <label className="asf-lbl">{t("eDiscovery.custodianRole")}</label>
                          <input className="asf-input" value={newCustodian.role}
                            onChange={(e) => setNewCustodian({ ...newCustodian, role: e.target.value })} />
                        </div>
                        <button className="asf-cta" type="submit" disabled={busy || !newCustodian.displayName.trim()}>
                          {t("eDiscovery.custodianAdd")}
                        </button>
                      </div>
                      <p className="ed-hint" style={{ margin: "10px 0 0" }}>{t("eDiscovery.custodianIdentifiersHint")}</p>
                    </form>
                  )}
                </div>

                {/* Collection */}
                <div className="asf-card">
                  <div className="ed-cardhd"><h2>{t("eDiscovery.collection")}</h2></div>
                  <p className="ed-hint">{t("eDiscovery.collectionHint")}</p>
                  <div className="asf-grid">
                    <div className="asf-field" style={{ flex: "1 1 190px" }}>
                      <label className="asf-lbl">{t("eDiscovery.database")}</label>
                      <Dropdown icon="🗄" value={collect.dbName} placeholder={t("eDiscovery.selectDatabase")}
                        onChange={(v) => setCollect({ ...collect, dbName: v })}
                        options={databases.map((d) => ({ value: d.name, label: d.displayName || d.name }))} />
                    </div>
                    <div className="asf-field" style={{ flex: "2 1 260px" }}>
                      <label className="asf-lbl">{t("eDiscovery.filter")}</label>
                      <input className="asf-input" value={collect.conditionJson}
                        placeholder={"{'DocumentDate': {'$gte': '2020-01-01'}}"}
                        onChange={(e) => setCollect({ ...collect, conditionJson: e.target.value })} />
                    </div>
                    <button className="asf-btn" onClick={onPreviewCollect}
                      disabled={busy || isClosed || !collect.dbName || scopeIncomplete}>
                      {t("eDiscovery.preview")}
                    </button>
                    <button className="asf-cta" onClick={onCollect}
                      disabled={busy || isClosed || !collect.dbName || scopeIncomplete}>
                      {t("eDiscovery.collect")}
                    </button>
                  </div>

                  {/* Custodian scope. Only offered once the matter HAS custodians — a scope control with
                      nothing to scope by would suggest a narrowing that cannot happen. */}
                  {activeCustodians.length > 0 && (
                    <div className="asf-grid" style={{ marginTop: 16, alignItems: "flex-start" }}>
                      <div className="asf-field" style={{ flex: "0 0 auto" }}>
                        <label className="asf-lbl">{t("eDiscovery.custodianScope")}</label>
                        <SegControl value={scopeMode} onChange={setScopeMode} options={[
                          { value: "none", label: t("eDiscovery.scopeNone") },
                          { value: "all", label: t("eDiscovery.scopeAll") },
                          { value: "selected", label: t("eDiscovery.scopeSelected") },
                        ]} />
                      </div>
                      {scopeMode === "selected" && (
                        <div className="asf-field" style={{ flex: "1 1 280px" }}>
                          <label className="asf-lbl">{t("eDiscovery.scopeCustodians")}</label>
                          <div className="ed-pick">
                            {activeCustodians.map((c) => (
                              <label key={c.id}>
                                <input type="checkbox" checked={scopeIds.includes(c.id)}
                                  onChange={(e) => setScopeIds((prev) => e.target.checked
                                    ? [...prev, c.id]
                                    : prev.filter((x) => x !== c.id))} />
                                {c.displayName}
                                <span className="ed-pick__meta">{c.identifierCount ?? 0}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {scopeMode !== "none" && (
                    <div className="ed-note ed-note--warn" style={{ marginTop: 14, marginBottom: 0 }}>
                      {scopeIncomplete ? t("eDiscovery.scopeIncomplete") : t("eDiscovery.scopeActiveNotice")}
                    </div>
                  )}
                  {collectPreview && (
                    <>
                      {/* A CLOSED source database collects fine but can never be produced from — retrieval
                          resolves the output format through DBInfo filtered on !IsClosed, so it yields no
                          bytes. Said here, at the moment the volume decision is made, rather than at
                          production time with a reviewed set already built. */}
                      {collectPreview.sourceClosed && collectPreview.sourceWarning && (
                        <div className="ed-note ed-note--warn" style={{ marginTop: 14, marginBottom: 0 }}>
                          {t("eDiscovery.sourceClosed", {
                            defaultValue: collectPreview.sourceWarning.message,
                            db: collectPreview.sourceWarning.dbName,
                          })}
                        </div>
                      )}

                      <div className={`ed-note ${collectPreview.wouldExceedCap ? "ed-note--warn" : "ed-note--ok"}`}
                        style={{ marginTop: 14, marginBottom: 0 }}>
                        {collectPreview.wouldExceedCap
                          ? collectPreview.message
                          : t("eDiscovery.previewOk", { candidates: collectPreview.candidates, cap: collectPreview.itemCap })}
                      </div>

                      {/* The per-custodian breakdown, and the row that matters: a custodian matching NOTHING.
                          That is nearly always a mistyped identifier, and it is only visible here — once
                          collected, a review set missing one custodian looks complete. */}
                      {Array.isArray(collectPreview.custodians) && collectPreview.custodians.length > 0 && (
                        <div className="ed-scroll" style={{ marginTop: 14 }}>
                          <table className="ed-table">
                            <thead>
                              <tr>
                                <th>{t("eDiscovery.custodianName")}</th>
                                <th>{t("eDiscovery.custodianIdentifiers")}</th>
                                <th style={{ textAlign: "right" }}>{t("eDiscovery.custodianCandidates")}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {collectPreview.custodians.map((c) => (
                                <tr key={c.custodianId} className={c.matchedNothing ? "ed-zero" : undefined}>
                                  <td>
                                    {c.displayName}
                                    {c.matchedNothing && (
                                      <div className="ed-meta" style={{ color: "#92400e" }}>
                                        {t("eDiscovery.custodianMatchedNothing")}
                                      </div>
                                    )}
                                  </td>
                                  <td className="ed-cust__ids">{(c.identifiers || []).join("; ")}</td>
                                  <td style={{ textAlign: "right", fontWeight: 700 }}>{c.candidates}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </>
                  )}
                </div>

                {/* Review */}
                <div className="asf-card">
                  <div className="ed-cardhd">
                    <h2>{t("eDiscovery.review")}</h2>
                    <div style={{ minWidth: 200 }}>
                      <Dropdown value={itemFilter} onChange={setItemFilter} placeholder={t("eDiscovery.allCodings")}
                        options={[{ value: "", label: t("eDiscovery.allCodings") },
                          ...codingOptions.map((c) => ({ value: c, label: t(`eDiscovery.coding.${c}`) }))]} />
                    </div>
                  </div>
                  {items.length === 0 ? <EmptyState icon="🔍" title={t("eDiscovery.noItems")} /> : (
                    <div className="ed-scroll">
                      <table className="ed-table">
                        <thead>
                          <tr>
                            <th>{t("eDiscovery.archiveNo")}</th>
                            <th>{t("eDiscovery.database")}</th>
                            <th>{t("eDiscovery.documentDate")}</th>
                            <th>{t("eDiscovery.custodianCol")}</th>
                            <th>{t("eDiscovery.codingCol")}</th>
                            <th>{t("eDiscovery.reviewedBy")}</th>
                            <th>{t("eDiscovery.actions")}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {items.map((i) => (
                            <tr key={i.id}>
                              <td>{i.archiveNo || i.documentId}</td>
                              <td><span className="asf-chip">{i.dbName}</span></td>
                              <td>{i.documentDate ? String(i.documentDate).slice(0, 10) : ""}</td>
                              <td>
                                {i.custodianId
                                  ? (custodians.find((c) => c.id === i.custodianId)?.displayName || i.custodianValue)
                                  : <span style={{ color: "#cbd5e1" }}>—</span>}
                                {i.custodianValue && (
                                  <div className="ed-meta" title={t("eDiscovery.custodianValueHint")}>{i.custodianValue}</div>
                                )}
                              </td>
                              <td>
                                <Pill value={i.coding} />
                                {i.privilegeBasis && <div className="ed-meta">{i.privilegeBasis}</div>}
                              </td>
                              <td>{i.reviewedBy || ""}</td>
                              <td style={{ minWidth: 170 }}>
                                <Dropdown value="" disabled={busy || isClosed} placeholder={t("eDiscovery.setCoding")}
                                  onChange={(v) => { if (v) onCode(i.id, v); }}
                                  options={["Responsive", "NotResponsive", "Privileged", "NeedsReview"]
                                    .map((c) => ({ value: c, label: t(`eDiscovery.coding.${c}`) }))} />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                {/* Privilege log */}
                {privilegeLog.length > 0 && (
                  <div className="asf-card">
                    <div className="ed-cardhd"><h2>{t("eDiscovery.privilegeLog")}</h2></div>
                    <p className="ed-hint">{t("eDiscovery.privilegeLogHint")}</p>
                    <div className="ed-scroll">
                      <table className="ed-table">
                        <thead>
                          <tr>
                            <th>{t("eDiscovery.archiveNo")}</th>
                            <th>{t("eDiscovery.basis")}</th>
                            <th>{t("eDiscovery.reviewedBy")}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {privilegeLog.map((p) => (
                            <tr key={p.id}>
                              <td>{p.archiveNo || p.documentId}</td>
                              <td>{p.privilegeBasis}</td>
                              <td>{p.reviewedBy}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Production */}
                <div className="asf-card">
                  <div className="ed-cardhd"><h2>{t("eDiscovery.production")}</h2></div>
                  <p className="ed-hint">{t("eDiscovery.productionHint")}</p>

                  {productions.length > 0 && (
                    <div className="ed-scroll" style={{ marginBottom: 16 }}>
                      <table className="ed-table">
                        <thead>
                          <tr>
                            <th>{t("eDiscovery.name")}</th>
                            <th>{t("eDiscovery.status")}</th>
                            <th>{t("eDiscovery.recipient")}</th>
                            <th>{t("eDiscovery.producedWithheld")}</th>
                            <th>{t("eDiscovery.bates")}</th>
                            <th>{t("eDiscovery.certificate")}</th>
                            <th>{t("eDiscovery.actions")}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {productions.map((p) => {
                            const st = String(p.status || "").toLowerCase();
                            return (
                              <tr key={p.id}>
                                <td>{p.name}</td>
                                <td><Pill value={p.status} />
                                  {p.error && <div className="ed-meta">{p.error}</div>}</td>
                                <td>{p.recipient || ""}</td>
                                <td>{p.docsProduced ?? "—"} / {p.docsWithheld ?? "—"}</td>
                                <td>{p.batesEnd ? `${p.batesStart}–${p.batesEnd}` : p.batesStart}</td>
                                <td>{p.certNumber ? <span className="asf-chip">{p.certNumber}</span> : "—"}</td>
                                <td>
                                  <div className="ed-actions">
                                    <button className="asf-btn ed-sm" disabled={busy}
                                      onClick={() => onProductionAction(p.id, "preview")}>{t("eDiscovery.scope")}</button>
                                    {st === "proposed" && (
                                      <>
                                        <button className="asf-btn ed-sm" disabled={busy}
                                          onClick={() => onProductionAction(p.id, "approve")}>{t("eDiscovery.approve")}</button>
                                        <button className="asf-btn ed-sm ed-danger" disabled={busy}
                                          onClick={() => onProductionAction(p.id, "reject")}>{t("eDiscovery.reject")}</button>
                                      </>
                                    )}
                                    {st === "approved" && (
                                      <button className="asf-cta ed-sm" disabled={busy}
                                        onClick={() => onProductionAction(p.id, "produce")}>{t("eDiscovery.produce")}</button>
                                    )}
                                    {p.certNumber && (
                                      <button className="asf-btn ed-sm" disabled={busy}
                                        onClick={() => onProductionAction(p.id, "cert")}>{t("eDiscovery.verify")}</button>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {!isClosed && (
                    <form onSubmit={onProposeProduction}>
                      <div className="asf-grid">
                        <div className="asf-field" style={{ flex: "1 1 170px" }}>
                          <label className="asf-lbl">{t("eDiscovery.name")}</label>
                          <input className="asf-input" value={prod.name}
                            onChange={(e) => setProd({ ...prod, name: e.target.value })} />
                        </div>
                        <div className="asf-field" style={{ flex: "1 1 170px" }}>
                          <label className="asf-lbl">{t("eDiscovery.recipient")}</label>
                          <input className="asf-input" value={prod.recipient}
                            onChange={(e) => setProd({ ...prod, recipient: e.target.value })} />
                        </div>
                        <div className="asf-field" style={{ flex: "1 1 220px" }}>
                          <label className="asf-lbl">{t("eDiscovery.includeCodings")}</label>
                          <Dropdown value={prod.includeCodings}
                            onChange={(v) => setProd({ ...prod, includeCodings: v })}
                            options={[
                              { value: "Responsive", label: t("eDiscovery.coding.Responsive") },
                              { value: "Responsive,NotResponsive",
                                label: `${t("eDiscovery.coding.Responsive")} + ${t("eDiscovery.coding.NotResponsive")}` },
                            ]} />
                        </div>
                      </div>
                      <div className="asf-grid" style={{ marginTop: 12 }}>
                        <div className="asf-field" style={{ flex: "1 1 150px" }}>
                          <label className="asf-lbl">{t("eDiscovery.batesPrefix")}</label>
                          <input className="asf-input" value={prod.batesPrefix}
                            onChange={(e) => setProd({ ...prod, batesPrefix: e.target.value })} />
                        </div>
                        <div className="asf-field" style={{ flex: "1 1 120px" }}>
                          <label className="asf-lbl">{t("eDiscovery.batesStart")}</label>
                          <input className="asf-input" type="number" min="0" value={prod.batesStart}
                            onChange={(e) => setProd({ ...prod, batesStart: Number(e.target.value) })} />
                        </div>
                        <div className="asf-field" style={{ flex: "1 1 220px" }}>
                          <label className="asf-lbl">{t("eDiscovery.targetLocation")}</label>
                          <Dropdown value={prod.targetLocationId} placeholder={t("eDiscovery.selectLocation")}
                            onChange={(v) => setProd({ ...prod, targetLocationId: v })}
                            options={[{ value: "", label: t("eDiscovery.selectLocation") },
                              ...locations.map((l) => ({ value: String(l.id), label: `${l.name} (${l.kind})` }))]} />
                        </div>
                      </div>
                      <div className="asf-grid" style={{ marginTop: 16, alignItems: "center" }}>
                        <ToggleSwitch checked={prod.excludePrivileged} label={t("eDiscovery.excludePrivileged")}
                          onChange={(v) => setProd({ ...prod, excludePrivileged: v })} />
                        <ToggleSwitch checked={prod.applyRedactions} label={t("eDiscovery.applyRedactions")}
                          onChange={(v) => setProd({ ...prod, applyRedactions: v })} />
                        <button className="asf-cta" type="submit" disabled={busy} style={{ marginLeft: "auto" }}>
                          {t("eDiscovery.proposeProduction")}
                        </button>
                      </div>
                      {!prod.excludePrivileged && (
                        <div className="ed-note ed-note--warn" style={{ marginTop: 14, marginBottom: 0 }}>
                          {t("eDiscovery.excludePrivilegedWarning")}
                        </div>
                      )}
                      {!prod.applyRedactions && (
                        <div className="ed-note ed-note--warn" style={{ marginTop: 14, marginBottom: 0 }}>
                          {t("eDiscovery.applyRedactionsWarning")}
                        </div>
                      )}
                    </form>
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        {/* ── Custodian field mapping (administrators) ──
            Per DATABASE, not per matter: the person who provisioned a document base knows which of its search
            columns carries custodian identity, and every later matter inherits that answer. Writes are
            DIXI_ADMIN-only server-side too — a mapping decides what a collection sweeps up. */}
        {isAdmin && (
          <div className="asf-card" style={{ marginTop: 4 }}>
            <div className="ed-cardhd">
              <h2>{t("eDiscovery.fieldMapTitle")}</h2>
              <div style={{ minWidth: 220 }}>
                <Dropdown icon="🗄" value={mapDbName} onChange={setMapDbName}
                  placeholder={t("eDiscovery.selectDatabase")}
                  options={databases.map((d) => ({ value: d.name, label: d.displayName || d.name }))} />
              </div>
            </div>
            <p className="ed-hint">{t("eDiscovery.fieldMapHint")}</p>

            {!mapDbName ? (
              <EmptyState icon="🧭" title={t("eDiscovery.fieldMapPickDb")} />
            ) : (
              <>
                {fieldMaps.filter((m) => m.isActive).length === 0 ? (
                  <div className="ed-note ed-note--warn" style={{ marginBottom: 14 }}>
                    {t("eDiscovery.fieldMapNone")}
                  </div>
                ) : (
                  <div className="ed-scroll" style={{ marginBottom: 16 }}>
                    <table className="ed-table">
                      <thead>
                        <tr>
                          <th>{t("eDiscovery.fieldMapColumn")}</th>
                          <th>{t("eDiscovery.fieldMapMode")}</th>
                          <th>{t("eDiscovery.fieldMapDescription")}</th>
                          <th>{t("eDiscovery.actions")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {fieldMaps.filter((m) => m.isActive).map((m) => (
                          <tr key={m.id}>
                            <td className="ed-cust__ids">{m.fieldName}</td>
                            <td style={{ minWidth: 190 }}>
                              <Dropdown value={m.matchMode} disabled={busy}
                                onChange={(v) => onChangeFieldMapMode(m, v)}
                                options={[
                                  { value: "Exact", label: t("eDiscovery.modeExact") },
                                  { value: "EqualsIgnoreCase", label: t("eDiscovery.modeEqualsIgnoreCase") },
                                  { value: "Contains", label: t("eDiscovery.modeContains") },
                                ]} />
                            </td>
                            <td>
                            {m.description || ""}
                            <div className="ed-meta">{t(`eDiscovery.modeHint${m.matchMode}`, { defaultValue: "" })}</div>
                          </td>
                            <td>
                              <button className="asf-btn ed-sm" disabled={busy}
                                onClick={() => onRemoveFieldMap(m)}>{t("eDiscovery.fieldMapRemove")}</button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                <form onSubmit={onAddFieldMap}>
                  <div className="asf-grid">
                    <div className="asf-field" style={{ flex: "1 1 220px" }}>
                      <label className="asf-lbl">{t("eDiscovery.fieldMapColumn")}</label>
                      {mapColumns.length > 0 ? (
                        <Dropdown value={newMap.fieldName} placeholder={t("eDiscovery.fieldMapSelectColumn")}
                          onChange={(v) => setNewMap({ ...newMap, fieldName: v })}
                          options={mapColumns.map((c) => ({ value: c, label: c }))} />
                      ) : (
                        <input className="asf-input" value={newMap.fieldName}
                          placeholder={t("eDiscovery.fieldMapSelectColumn")}
                          onChange={(e) => setNewMap({ ...newMap, fieldName: e.target.value })} />
                      )}
                    </div>
                    <div className="asf-field" style={{ flex: "1 1 220px" }}>
                      <label className="asf-lbl">{t("eDiscovery.fieldMapMode")}</label>
                      <Dropdown value={newMap.matchMode} onChange={(v) => setNewMap({ ...newMap, matchMode: v })}
                        options={[
                          { value: "Exact", label: t("eDiscovery.modeExact") },
                          { value: "EqualsIgnoreCase", label: t("eDiscovery.modeEqualsIgnoreCase") },
                          { value: "Contains", label: t("eDiscovery.modeContains") },
                        ]} />
                    </div>
                    <div className="asf-field" style={{ flex: "2 1 200px" }}>
                      <label className="asf-lbl">{t("eDiscovery.fieldMapDescription")}</label>
                      <input className="asf-input" value={newMap.description}
                        onChange={(e) => setNewMap({ ...newMap, description: e.target.value })} />
                    </div>
                    <button className="asf-cta" type="submit" disabled={busy || !newMap.fieldName}>
                      {t("eDiscovery.fieldMapAdd")}
                    </button>
                  </div>
                  <p className="ed-hint" style={{ margin: "10px 0 0" }}>
                    {t(`eDiscovery.modeHint${newMap.matchMode}`)}
                  </p>
                </form>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
